import { useEffect, useState } from "react";
import { TrendingUp, Sparkles, ArrowDownToLine } from "lucide-react";
import api from "../lib/api";

/**
 * Tiny SVG sparkline — no library, ~30 lines. Renders a smooth area + line
 * over `values`, scaled to the parent box. `accent` controls stroke + fill.
 */
function Sparkline({ values, accent = "#0f766e", height = 28, ariaLabel }) {
  if (!values || values.length === 0) return null;
  const max = Math.max(...values, 1);
  const w = 120;
  const h = height;
  const stepX = values.length > 1 ? w / (values.length - 1) : 0;
  const points = values.map((v, i) => [i * stepX, h - (v / max) * (h - 2) - 1]);
  const linePath = points.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
  const areaPath = `${linePath} L${w},${h} L0,${h} Z`;
  const last = points[points.length - 1];
  return (
    <svg
      viewBox={`0 0 ${w} ${h}`}
      preserveAspectRatio="none"
      className="w-full mt-1.5 block"
      role="img"
      aria-label={ariaLabel || "trend"}
    >
      <path d={areaPath} fill={accent} fillOpacity="0.12" />
      <path d={linePath} fill="none" stroke={accent} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round" />
      <circle cx={last[0]} cy={last[1]} r="2" fill={accent} />
    </svg>
  );
}

/**
 * Admin-only widget on the Dashboard showing the 30-day retention funnel —
 * how many users saw the cancel offer, how many accepted, and the dollars
 * recovered from annual upsells.
 *
 * Data source: GET /api/admin/retention/metrics (new in iter35).
 * Quietly hides on any error so a non-admin who lands here doesn't see noise.
 */
export default function RetentionSavesWidget() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/admin/retention/metrics")
      .then((r) => {
        if (!cancelled) setData(r.data);
      })
      .catch(() => {
        if (!cancelled) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (error || !data) return null;

  const dollars = data.dollars_recovered || 0;
  const accept = data.accept_rate_pct || 0;
  const cancel = data.cancel_rate_pct || 0;
  const series = data.weekly_series || {};
  // Derive a simple weekly accept-rate series so the trend matches the KPI
  // card. If a week has zero offers we just leave the bucket at 0.
  const acceptSeries = (series.offered || []).map((o, i) =>
    o > 0 ? Math.min(100, (series.accepted[i] / o) * 100) : 0
  );
  const cancelSeries = (series.offered || []).map((o, i) =>
    o > 0 ? Math.min(100, (series.cancelled[i] / o) * 100) : 0
  );

  return (
    <div
      className="bg-gradient-to-br from-emerald-50 to-emerald-100 border border-emerald-200 rounded-sm p-5"
      data-testid="retention-saves-widget"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-emerald-700" />
          <h3 className="text-sm font-bold text-slate-900">Retention saves · last 30 days</h3>
          <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold ml-2">
            8-week trend below
          </span>
        </div>
        <a
          href="/dashboard/admin/revenue"
          className="text-xs text-slate-600 hover:text-slate-900"
          data-testid="retention-saves-revenue-link"
        >
          Revenue →
        </a>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div data-testid="retention-saves-dollars">
          <div className="text-[10px] uppercase tracking-widest text-emerald-700 font-bold">Dollars recovered</div>
          <div className="text-3xl font-bold text-slate-900 mt-1 font-mono tracking-tight" style={{ fontFamily: "Outfit" }}>
            ${Math.round(dollars).toLocaleString()}
          </div>
          <div className="text-[11px] text-emerald-700 mt-0.5 inline-flex items-center gap-1">
            <TrendingUp className="w-3 h-3" />
            from {data.accepted || 0} annual upsell{(data.accepted || 0) === 1 ? "" : "s"}
          </div>
          <Sparkline values={series.dollars_recovered || []} accent="#047857" ariaLabel="Weekly dollars recovered" />
        </div>

        <div data-testid="retention-saves-offered">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Offers shown</div>
          <div className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: "Outfit" }}>{data.offered || 0}</div>
          <div className="text-[11px] text-slate-500 mt-0.5">cancel-intent triggers</div>
          <Sparkline values={series.offered || []} accent="#475569" ariaLabel="Weekly offers shown" />
        </div>

        <div data-testid="retention-saves-accept-rate">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Accept rate</div>
          <div className="text-2xl font-bold text-emerald-800 mt-1" style={{ fontFamily: "Outfit" }}>{accept}%</div>
          <div className="text-[11px] text-slate-500 mt-0.5">{data.accepted || 0} took the deal</div>
          <Sparkline values={acceptSeries} accent="#047857" ariaLabel="Weekly accept rate" />
        </div>

        <div data-testid="retention-saves-cancel-rate">
          <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Final cancel rate</div>
          <div className="text-2xl font-bold text-rose-700 mt-1" style={{ fontFamily: "Outfit" }}>{cancel}%</div>
          <div className="text-[11px] text-slate-500 mt-0.5 inline-flex items-center gap-1">
            <ArrowDownToLine className="w-3 h-3" /> {data.cancelled || 0} churned
          </div>
          <Sparkline values={cancelSeries} accent="#be123c" ariaLabel="Weekly cancel rate" />
        </div>
      </div>
    </div>
  );
}
